import React, { useMemo } from "react";
import { resolveEventTimeMs } from "../../utils/chart";

const iconMap = {
  Counter1: "/icons/Counter_1.png",
  Counter2: "/icons/Counter_2.png",
  Try: "/icons/Try.png",
};

const normalizeEventValue = (eventValue) => {
  const raw = String(eventValue || "").trim();
  if (!raw) return "";
  const key = raw.replace(/\s+/g, "").replace(/_/g, "").toLowerCase();
  if (key === "counter1" || key === "c1" || key === "counterone") return "Counter1";
  if (key === "counter2" || key === "c2" || key === "countertwo") return "Counter2";
  if (key === "try" || key === "attempt") return "Try";
  return raw;
};

const buildQuarterTicks = (start, end) => {
  const ticks = [];
  for (let t = start.getTime(); t <= end.getTime(); t += 15 * 60 * 1000) {
    ticks.push(t);
  }
  return ticks;
};

const buildBucketsFromTimeMs = (events = []) => {
  if (!events.length) return [];
  const hourMs = 60 * 60 * 1000;
  const bucketsByHour = new Map();
  events.forEach((event) => {
    const eventTime = event.timeMs;
    if (!Number.isFinite(eventTime)) return;
    const hourStartMs = Math.floor(eventTime / hourMs) * hourMs;
    const bucket =
      bucketsByHour.get(hourStartMs) ||
      {
        start: new Date(hourStartMs),
        end: new Date(hourStartMs + hourMs),
        events: [],
      };
    bucket.events.push(event);
    bucketsByHour.set(hourStartMs, bucket);
  });
  return Array.from(bucketsByHour.values()).sort(
    (a, b) => b.start.getTime() - a.start.getTime(),
  );
};

const SessionEventsChart = ({ events = [], userName, avatar }) => {
  if (!events.length) {
    return <div className="chart">No events yet.</div>;
  }
  const eventsWithTime = events.map((event) => {
    const createdAtMs = Date.parse(event?.createdAt);
    const fallbackMs = resolveEventTimeMs(event);
    const timeMs = Number.isFinite(createdAtMs) ? createdAtMs : fallbackMs;
    return {
      ...event,
      timeMs,
      _timeSource: Number.isFinite(createdAtMs) ? "createdAt" : "fallback",
      eventValue: normalizeEventValue(
        event.eventValue ?? event.type ?? event.eventType ?? event.name,
      ),
    };
  });
  const chartData = { buckets: buildBucketsFromTimeMs(eventsWithTime) };

  if (!chartData.buckets.length) {
    return <div className="chart">No events yet.</div>;
  }

  return (
    <div className="hour-stack">
      {chartData.buckets.map((bucket) => {
        const quarterTicks = buildQuarterTicks(bucket.start, bucket.end);
        const rowHeight = 40;
        const axisHeight = 28;
        const plotHeight = rowHeight;
        const startMs = bucket.start.getTime();
        const endMs = bucket.end.getTime();

        const counters = bucket.events.reduce(
          (acc, e) => {
            if (e.eventValue === "Counter1") acc.c1 += 1;
            if (e.eventValue === "Counter2") acc.c2 += 1;
            if (e.eventValue === "Try") acc.try += 1;
            acc.total += 1;
            return acc;
          },
          { c1: 0, c2: 0, try: 0, total: 0 },
        );

        const toPercent = (time) => {
          if (!Number.isFinite(time)) return 0;
          const pct = ((time - startMs) / (endMs - startMs)) * 100;
          return Math.max(0, Math.min(100, pct));
        };

        return (
          <div key={bucket.start.toISOString()} className="hour-block">
            <div className="hour-title">
              {bucket.start.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}{" "}
              -{" "}
              {bucket.end.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
            </div>
            <div
              className="hour-layout"
              style={{
                "--row-h": `${rowHeight}px`,
                "--axis-h": `${axisHeight}px`,
              }}
            >
              <div className="hour-stats">
                <div className="hour-stats-row">
                  <img
                    className="hour-stats-avatar"
                    src={avatar || "/icons/Logo_PNG.png"}
                    alt={userName || "User"}
                  />
                  <div className="hour-stats-name">{userName || "You"}</div>
                  <div className="hour-stats-badge c1">{counters.c1}</div>
                  <div className="hour-stats-badge c2">{counters.c2}</div>
                  <div className="hour-stats-badge try">{counters.try}</div>
                  <div className="hour-stats-badge total">{counters.total}</div>
                </div>
              </div>
              <div className="team-chart">
                <div
                  className="team-plot"
                  style={{ height: `${plotHeight}px` }}
                >
                  {quarterTicks.map((t) => (
                    <div
                      key={t}
                      className="team-grid-line"
                      style={
                        t === bucket.end.getTime()
                          ? { right: 0 }
                          : { left: `${toPercent(t)}%` }
                      }
                    />
                  ))}
                  <div
                    className="team-row"
                    style={{ top: 0, height: rowHeight }}
                  >
                    {bucket.events.map((e, eIdx) => {
                      const eventValue = e.eventValue;
                      const icon = iconMap[eventValue] || "/icons/Try.png";
                      const size = eventValue === "Try" ? 7 : 14;
                      const timeMs = e.timeMs;
                      if (!Number.isFinite(timeMs)) return null;
                      return (
                        <img
                          key={eIdx}
                          src={icon}
                          alt={eventValue || "Event"}
                          className="team-event-icon"
                          style={{
                            left: `${toPercent(timeMs)}%`,
                            width: size,
                            height: size,
                            marginLeft: -size / 2,
                          }}
                          title={`${eventValue || "Event"} @ ${new Date(
                            timeMs,
                          ).toLocaleTimeString()}`}
                        />
                      );
                    })}
                  </div>
                </div>
                <div
                  className="team-axis"
                  style={{ height: `${axisHeight}px` }}
                >
                  {quarterTicks.map((t) => (
                    <div
                      key={t}
                      className="team-axis-tick"
                      style={
                        t === bucket.end.getTime()
                          ? { right: 0 }
                          : { left: `${toPercent(t)}%` }
                      }
                    >
                      {new Date(t).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SessionEventsChart;
